import { useState } from "react";

import { faFilter } from "@fortawesome/pro-regular-svg-icons";
import { faFilter as faFilterSolid } from "@fortawesome/pro-solid-svg-icons";
import {
  CloseButton,
  Popover,
  PopoverButton,
  PopoverPanel,
} from "@headlessui/react";
import { twJoin } from "tailwind-merge";

import { Button } from "src/base-components/Button";
import { Icon } from "src/base-components/Icon";
import { Dropdown } from "src/design-system/Dropdown";

type Props = {
  dataLoc?: string;
  isOpen?: boolean;
  description: string;
  children: React.ReactNode;
  isFiltering: boolean;
  isApplied?: boolean;
  onResetRequest: () => void;
  onApply: () => void;
  value: string;
  operator: string;
};

export const CustomDropdownFilter = ({
  dataLoc,
  description,
  children,
  isFiltering,
  isApplied,
  onResetRequest,
  onApply,
  value,
  operator,
}: Props) => {
  const [lastAppliedValue, setLastAppliedValue] = useState<string>(value);
  const [lastAppliedOperator, setLastAppliedOperator] =
    useState<string>(operator);

  const isApplyDisabled =
    value === lastAppliedValue && operator === lastAppliedOperator;
  const isResetDisabled = !isFiltering || value === "";

  return (
    <Popover className="relative">
      <PopoverButton className="border-0 bg-transparent" data-loc={dataLoc}>
        {({ open }) => (
          <Icon
            color={twJoin(
              "text-gray-500 hover:text-gray-700",
              (open || isFiltering) && "text-indigo-500",
            )}
            icon={open || isFiltering ? faFilterSolid : faFilter}
            size="2xs"
          />
        )}
      </PopoverButton>

      <PopoverPanel
        anchor={{
          to: "bottom end",
          gap: 10,
          padding: 12,
        }}
        as={Dropdown}
        data-loc={`${dataLoc}-dropdown`}
        transition
      >
        <div className="divide-y divide-gray-200">
          <div className="flex w-[240px] flex-col justify-between p-4">
            <h3 className="cursor-default items-center font-inter-semibold-13px">
              {description}
            </h3>
            <div className="pt-2">{children}</div>
          </div>

          <div className="flex justify-between p-4">
            <CloseButton
              as={Button}
              disabled={isResetDisabled}
              size="sm"
              variant="secondary"
              onClick={onResetRequest}
            >
              Reset
            </CloseButton>
            <CloseButton
              as={Button}
              dataLoc={`${dataLoc}-apply-button`}
              disabled={isApplyDisabled || isApplied}
              size="sm"
              variant="primary"
              onClick={() => {
                setLastAppliedValue(value);
                setLastAppliedOperator(operator);
                onApply();
              }}
            >
              Apply
            </CloseButton>
          </div>
        </div>
      </PopoverPanel>
    </Popover>
  );
};
